import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, Check, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../api/client';

const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newEmail) {
      toast.error('Please enter a new email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.put('/auth/update-email', { new_email: newEmail });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || 'Failed to update email');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  // Only super_admin can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-secondary-offWhite p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-primary-black mb-2">Access Denied</h2>
          <p className="text-secondary-gray">Only Super Admin can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-primary-black">Account Settings</h1>
              <p className="text-secondary-gray text-sm mt-1">
                Update your account email and password
              </p>
            </div>
          </div>
        </div>

        {/* Current Account Info */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-accent-gold/10 rounded-lg">
              <Shield className="w-5 h-5 text-accent-gold" />
            </div>
            <h2 className="text-lg font-semibold text-primary-black">Current Account</h2>
          </div>
          
          <div className="grid gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-secondary-gray mb-1">Email (Username)</p>
              <p className="font-medium text-primary-black">{user?.email || 'Not set'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-secondary-gray mb-1">Name</p>
              <p className="font-medium text-primary-black">{user?.full_name || user?.username}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-secondary-gray mb-1">Role</p>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-gold/10 text-accent-gold">
                Super Admin
              </span>
            </div>
          </div>
        </div>

        {/* Update Email Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-primary-black">Update Email & Password</h2>
          </div>

          {user?.is_sso ? (
            <div className="text-center py-6">
               <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Profile Managed by Platform</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Your account is managed via Single Sign-On (SSO). Please visit your One Manager Platform profile to update your email or password.
              </p>
              <a
                href={`${import.meta.env.VITE_PLATFORM_URL || 'http://localhost:3005'}/settings/profile`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Go to Platform Profile
              </a>
            </div>
          ) : (
            success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-black mb-2">
                Email Updated Successfully!
              </h3>
              <p className="text-secondary-gray mb-4">
                Your email has been changed to <strong>{newEmail}</strong>.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-yellow-800 font-medium mb-2">Next Steps:</p>
                <ol className="text-sm text-yellow-800 list-decimal list-inside space-y-1">
                  <li>You will be logged out</li>
                  <li>Go to the <strong>Login page</strong></li>
                  <li>Click <strong>"Forgot Password"</strong></li>
                  <li>Enter your new email: <strong>{newEmail}</strong></li>
                  <li>Set your new password</li>
                </ol>
              </div>
              <button
                onClick={() => {
                  // Logout and redirect to login
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  window.location.href = '/login';
                }}
                className="px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                Go to Login Page
              </button>
            </div>
          ) : (
            <>
              <p className="text-secondary-gray text-sm mb-6">
                Enter a new email address. This will become your new username, and a password reset link 
                will be sent to this email. You'll need to click the link to set a new password.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="newemail@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Your email will be updated immediately</li>
                        <li>You will be logged out after updating</li>
                        <li>Use <strong>"Forgot Password"</strong> on the login page</li>
                        <li>Enter your new email to receive a reset link</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !newEmail}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Update Email & Send Reset Link
                    </>
                  )}
                </button>
              </form>
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
